import React, { useState } from 'react';
import { useAuthContext } from '../auth/AuthProvider';
import { User, MapPin, Key, Shield, Bell, Globe } from 'lucide-react';
import { Tab } from '@headlessui/react';
import { ProfileTab } from './tabs/ProfileTab';
import { LocationsTab } from './tabs/LocationsTab';
import { SecurityTab } from './tabs/SecurityTab';
import { PreferencesTab } from './tabs/PreferencesTab';
import { KYCTab } from './tabs/KYCTab';
import { motion } from 'framer-motion';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export function UserSettings() {
  const { user, profile } = useAuthContext();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const tabs = [
    { name: 'Profile', icon: User, component: ProfileTab },
    { name: 'KYC', icon: Shield, component: KYCTab },
    { name: 'Locations', icon: MapPin, component: LocationsTab },
    { name: 'Security', icon: Key, component: SecurityTab },
    { name: 'Preferences', icon: Globe, component: PreferencesTab }
  ];

  const CurrentTabComponent = tabs[selectedIndex].component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg min-h-[600px] border border-gray-100/50 backdrop-blur-sm"
        >
          <div className="flex flex-col lg:flex-row min-h-[600px]">
            {/* Left Sidebar */}
            <div className="lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-100">
              <div className="p-6">
                {/* User Info Card */}
                <div className="p-4 mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 p-0.5">
                        <div className="w-full h-full rounded-xl overflow-hidden bg-white">
                          {profile?.avatar_url ? (
                            <img
                              src={profile.avatar_url}
                              alt={profile.display_name || ''}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-6 h-6 text-blue-500" />
                            </div>
                          )}
                        </div>
                      </div>
                      {profile?.kyc_status === 'approved' && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                          <Shield className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {profile?.display_name || user?.email}
                      </p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className={`w-2 h-2 rounded-full ${
                      profile?.kyc_status === 'approved' ? 'bg-green-500' :
                      profile?.kyc_status === 'pending' ? 'bg-yellow-500' :
                      'bg-gray-500'
                    }`} />
                    <span className="text-gray-600 capitalize">
                      {profile?.role} Account
                    </span>
                  </div>
                  {profile?.kyc_status && (
                    <div className="mt-2 text-xs">
                      <span className={`px-2 py-1 rounded-full ${
                        profile.kyc_status === 'approved' ? 'bg-green-100 text-green-700' :
                        profile.kyc_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        KYC {profile.kyc_status}
                      </span>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                  <Tab.List className="flex flex-col space-y-1">
                    {tabs.map((tab) => (
                      <Tab
                        key={tab.name}
                        className={({ selected }) =>
                          classNames(
                            'flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all text-left focus:outline-none',
                            selected
                              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          )
                        }
                      >
                        <tab.icon className="w-5 h-5" />
                        {tab.name}
                      </Tab>
                    ))}
                  </Tab.List>
                </Tab.Group>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-6 lg:p-8">
              <motion.div
                key={selectedIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <CurrentTabComponent />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}